import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { detectType, isProductionMode } from 'src/js/utils';
import { MAIL } from 'newJs/config';
import inx from 'inx';
import { utils } from 'containers/Compose/utils';

const { MODE_NAMES: CMN } = MAIL.get('COMPOSE');
const MHF = MAIL.get('HEADER_FIELDS');
const mail = inx?.mail || {};
const params = mail.params || {};
const compose = params.compose || {};
const editor = compose.editor || {};
const identities = compose.identities || {};
const attachments = compose.attachments || {};
const spaceTotal = attachments.spaceTotal;
const headerVisibilityStateHandlers = (state, arg) => {
    const [payload, toggle] = arg;
    const argType = detectType(payload);

    const handlersList = {
        'Object'(list) {
            const oldState = state.headers;
            const newState = { ...state.headers };

            for (const name in list) {
                const val = list[name];
                newState[name] = {
                    ...oldState[name],
                    show: typeof val === 'boolean' ? val : !oldState[name].show
                };
            }
            return newState;
        },
        'String'(name, toggle) {
            // console.log(name, toggle);
            const oldState = state.headers[name];
            const newState = {
                ...oldState,
                show: typeof toggle === 'boolean' ? toggle : !oldState.show
            };
            return { ...state.headers, [name]: newState };
        }
    };

    return handlersList[argType](payload, toggle);
};

const store = (set) => ({
    currentMode: utils.getModeFromPathName(),
    editorPlainText: editor.plainText,
    requestReceipt: false,
    sendButtonDisabled: false,
    sendButtonLoading: false,
    saveButtonLoading: false,
    xsCCButtonVisible: false,
    attachments: {
        list: [],
        spaceOccupied: 0,
        spaceTotal: spaceTotal,
        lastToken: null,
        deletedList: []
    },
    progress: {},
    setProgress: (updateOrNewProgress) => set((state) => {
        // Determine the new progress state
        const newProgress = typeof updateOrNewProgress === 'function'
            ? updateOrNewProgress(state.progress)
            : updateOrNewProgress;
        return {
            ...state,
            progress: newProgress,
        };
    }),
    headers: {
        [MHF.FROM]: { show: true },
        [MHF.TO]: { show: true },
        [MHF.CC]: { show: false },
        [MHF.BCC]: { show: false },
        [MHF.SUBJECT]: { show: true },
    },
    identity: utils.getDefaultIdentity(identities),
    modal: {
        contacts: { clickedHeader: '', show: false },
        headerRemoval: { clickedHeader: '', show: false },
        limits: { show: false },
        sensitiveInfo: { show: false },
        spamReply: { show: false },
    },
    modalFileUpload: {
        show: false,
        uploadedFilesCount: 0,
        list: []
    },
    modalInboxFiles: {
        show: false,
        uploadedFilesCount: 0,
        list: []
    },
    sendLater: {
        show: false,
        sendAtRelativeMs: '',
    },
    actions: {
        setCurentMode: (modeName = CMN.COMPOSE) => set({ currentMode: modeName }),
        setIdentity: (current, params) => set((state) => {
            // console.log(current, params);
            return {
                identity: { ...state.identity, current, params }
            };
        }),
        toggleCCButton: (val) => set((state) => ({
            xsCCButtonVisible: typeof val === 'boolean' ? val : !state.xsCCButtonVisible
        })),
        toggleSendButtonDisabled: (val) => set((state) => ({
            sendButtonDisabled: typeof val === 'boolean' ? val : !state.sendButtonDisabled
        })),
        toggleSendButtonLoading: (val) => set((state) => ({
            sendButtonLoading: typeof val === 'boolean' ? val : !state.sendButtonLoading
        })),
        toggleSaveButtonLoading: (val) => set((state) => ({
            saveButtonLoading: typeof val === 'boolean' ? val : !state.saveButtonLoading
        })),
        toggleContactsModal: (clickedHeader = '') => set((state) => ({
            modal: {
                ...state.modal,
                contacts: {
                    clickedHeader,
                    show: !state.modal.contacts.show
                }
            }
        })),
        toggleHeaderRemovalModal: (clickedHeader = '', val) => set((state) => {
            // console.log(clickedHeader, val);
            const headerRemoval = {
                clickedHeader: val ? clickedHeader : '',
                show: typeof val === 'boolean' ? val : !state.modal.headerRemoval.show
            };
            return {
                modal: { ...state.modal, headerRemoval },
            };
        }),
        toggleHeaderVisibility: (...arg) => set((state) => {
            // console.log(arg);
            return { headers: headerVisibilityStateHandlers(state, arg) };
        }),
        toggleModal: (name, val) => set((state) => {
            if (typeof name !== 'string' || name?.length < 1) return state;

            const { modal } = state;

            return {
                modal: {
                    ...modal,
                    [name]: { show: typeof val === 'boolean' ? val : !modal[name].show }
                }
            };
        }),
        // Toggle "Request a read receipt" option for the current identity
        toggleRequestReceipt: (val) => set((state) => ({
            requestReceipt: typeof val === 'boolean' ? val : !state.requestReceipt
        })),
        // Toggle "Save sent" option for the current identity
        toggleSaveSent: () => set((state) => {
            const { identity: { params } } = state;
            const save_sent_mail = !params.save_sent_mail;
            return {
                identity: { ...state.identity, params: { ...params, save_sent_mail } }
            };
        }),
        toggleStateValue: (name, val) => set((state) => ({
            [name]: typeof val === 'boolean' ? val : !state[name]
        })),
        toggleModalFileUpload: () => set((state) => ({
            modalFileUpload: {
                ...state.modalFileUpload,
                show: !state.modalFileUpload.show
            }
        })),
        updateModalFileUpload: (list) => set((state) => ({
            modalFileUpload: {
                ...state.modalFileUpload,
                list: list.map((item, i) => ({
                    file: item,
                    error: '',
                    order: i + 1
                }))
            }
        })),
        setUploadedFilesCount: (increment = true, value = 1) => set((state) => ({
            modalFileUpload: {
                ...state.modalFileUpload,
                uploadedFilesCount: value === 0
                    ? 0
                    : increment
                        ? state.modalFileUpload.uploadedFilesCount + value
                        : Math.max(0, state.modalFileUpload.uploadedFilesCount - value)
            }
        })),
        toggleModalInboxFiles: () => set((state) => ({
            modalInboxFiles: {
                ...state.modalInboxFiles,
                show: !state.modalInboxFiles.show
            }
        })),
        setSendAtRelativeMs: (current) => set((state) => ({
            sendLater: {
                ...state.sendLater,
                sendAtRelativeMs: current
            }
        })),
        setLastToken: (val) => set((state) => {
            const { attachments } = state;
            return { attachments: {...attachments, lastToken: val } };
        }),
        addToDeleteAttachments: (item) => set((state) => ({
            attachments: {
                ...state.attachments,
                deletedList: [...state.attachments.deletedList, item],
            },
        })),
        updateAttachCount: {
            add: (arg) => set((state) => {
                const argType = detectType(arg);

                if (['Array', 'Object'].includes(argType)) {
                    const { attachments } = state;
                    const itemsArray = argType === 'Array' ? arg : [arg];
                    const list = [...itemsArray, ...attachments.list];
                    // When data comes from the localStorage, item.size is a string
                    const spaceOccupied = list.filter(item => item.isUploaded).reduce((acc, item) => acc + Number(item.file.size), 0);
                    return { attachments: { ...attachments, list, spaceOccupied } };
                }

                return state;
            }),
            enrichItem: (value) =>
                set((state) => {
                    const existingIndex = state.attachments.list.findIndex((item) => item.uid === value.uid);
                    let spaceOccupied = state.attachments.spaceOccupied;

                    // Calculate spaceOccupied only if the item is uploaded
                    if (value.isUploaded) {
                        // sum existing files and new uploaded files
                        spaceOccupied = state.attachments.list.reduce((acc, item) => {
                            return acc + (item.isUploaded ? Number(item.file.size) : 0) + (value.uid === item.uid && value.isUploaded ? Number(value.file.size) : 0);
                        }, 0);
                    }

                    if (existingIndex !== -1) {
                        // If item with same id exists, replace it
                        const newList = [...state.attachments.list];
                        newList[existingIndex] = value;
                        return {
                            ...state,
                            attachments: {
                                ...state.attachments,
                                list: newList,
                                spaceOccupied
                            }
                        };
                    } else {
                        // If item with same id doesn't exist, populate
                        return {
                            ...state,
                            attachments: {
                                ...state.attachments,
                                list: [...state.attachments.list, value],
                                spaceOccupied
                            }
                        };
                    }
                }),
            update: (list) => set((state) => {
                const { attachments } = state;
                const spaceOccupied = list.reduce((acc, item) => acc + Number(item.file.size), 0);
                return { attachments: { ...attachments, list:list, spaceOccupied } };
            }),
            remove: (id) => set((state) => {
                const { attachments } = state;
                let spaceOccupied = attachments.spaceOccupied;

                const list = attachments.list.filter((item) => {
                    if (item.uid !== id) return true;
                    spaceOccupied -= item.file.size;
                    return false;
                });
                return { attachments: { ...attachments, list, spaceOccupied } };
            }),
        },
    }
});

const useMailComposeStore = create(
    devtools(store, {
        enabled: !isProductionMode(),
        name: 'mail-compose-store',
        trace: true,
    })
);

// Export store for non-React usage
export const composeStore = {
    setSendAtRelativeMs: (current) => useMailComposeStore.getState().actions.setSendAtRelativeMs(current)
};
export const useModalState = () => useMailComposeStore((state) => state.modal);
export const useComposeStoreActions = () => useMailComposeStore((state) => state.actions);
export default useMailComposeStore;
